import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { PageLoader } from './components/ui/Misc';
import { RequireRole } from './lib/auth';

const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/auth/Login'));
const RegisterMerchant = lazy(() => import('./pages/auth/RegisterMerchant'));
const RegisterRider = lazy(() => import('./pages/auth/RegisterRider'));
const PublicApplicationStatus = lazy(() => import('./pages/rider/ApplicationStatus'));
const MyApplication = lazy(() => import('./pages/rider/ApplicationStatus').then((m) => ({ default: m.MyApplication })));
const Track = lazy(() => import('./pages/track/Track'));
const MerchantLayout = lazy(() => import('./apps/merchant/MerchantLayout'));
const MerchantOverview = lazy(() => import('./apps/merchant/Overview'));
const MerchantNew = lazy(() => import('./apps/merchant/NewDelivery'));
const MerchantOrders = lazy(() => import('./apps/merchant/Orders'));
const MerchantImport = lazy(() => import('./apps/merchant/BatchImport'));
const MerchantInventory = lazy(() => import('./apps/merchant/Inventory'));
const MerchantClubs = lazy(() => import('./apps/merchant/MyClubs'));
const MerchantCod = lazy(() => import('./apps/merchant/Cod'));
const MerchantCredits = lazy(() => import('./apps/merchant/Credits'));
const MerchantSettings = lazy(() => import('./apps/merchant/Settings'));
const RiderApp = lazy(() => import('./apps/rider/RiderApp'));
const RiderHome = lazy(() => import('./apps/rider/Home'));
const RiderActive = lazy(() => import('./apps/rider/Active'));
const RiderEarnings = lazy(() => import('./apps/rider/Earnings'));
const RiderProfile = lazy(() => import('./apps/rider/Profile'));
const AdminLayout = lazy(() => import('./apps/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./apps/admin/Dashboard'));
const AdminLive = lazy(() => import('./apps/admin/LiveMap'));
const AdminDispatch = lazy(() => import('./apps/admin/Dispatch'));
const AdminDisputes = lazy(() => import('./apps/admin/Disputes'));
const AdminRiders = lazy(() => import('./apps/admin/Riders'));
const AdminOnboarding = lazy(() => import('./apps/admin/Onboarding'));
const AdminKyc = lazy(() => import('./apps/admin/MerchantKyc'));
const AdminPricing = lazy(() => import('./apps/admin/Pricing'));
const AdminLogin = lazy(() => import('./pages/auth/AdminLogin'));
const AdminMessaging = lazy(() => import('./apps/admin/Messaging'));
const AdminCities = lazy(() => import('./apps/admin/Cities'));
const AdminCredits = lazy(() => import('./apps/admin/MerchantCredits'));
const AdminMonitor = lazy(() => import('./apps/admin/RiderMonitor'));
const AdminPushTest = lazy(() => import('./apps/admin/PushTest'));
const AdminMessageLog = lazy(() => import('./apps/admin/MessageLog'));
const AdminMerchantSms = lazy(() => import('./apps/admin/MerchantSms'));
const AdminRequirements = lazy(() => import('./apps/admin/Requirements'));
const AdminClubs = lazy(() => import('./apps/admin/RiderClubs'));
const AdminAudit = lazy(() => import('./apps/admin/Audit'));
const TrackLookup = lazy(() => import('./pages/track/Track').then((m) => ({ default: m.TrackLookup })));

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/register/merchant" element={<RegisterMerchant />} />
        <Route path="/register/rider" element={<RegisterRider />} />
        <Route path="/application-status" element={<PublicApplicationStatus />} />
        <Route path="/track" element={<TrackLookup />} />
        <Route path="/track/:code" element={<Track />} />
        <Route path="/merchant" element={<RequireRole role="merchant"><MerchantLayout /></RequireRole>}>
          <Route index element={<MerchantOverview />} />
          <Route path="new" element={<MerchantNew />} />
          <Route path="orders" element={<MerchantOrders />} />
          <Route path="credits" element={<MerchantCredits />} />
          <Route path="inventory" element={<MerchantInventory />} />
          <Route path="groups" element={<MerchantClubs />} />
          <Route path="import" element={<MerchantImport />} />
          <Route path="cod" element={<MerchantCod />} />
          <Route path="settings" element={<MerchantSettings />} />
        </Route>
        <Route path="/rider" element={<RequireRole role="rider"><RiderApp /></RequireRole>}>
          <Route index element={<RiderHome />} />
          <Route path="active" element={<RiderActive />} />
          <Route path="earnings" element={<RiderEarnings />} />
          <Route path="profile" element={<RiderProfile />} />
        </Route>
        <Route path="/admin" element={<RequireRole role="admin"><AdminLayout /></RequireRole>}>
          <Route index element={<AdminDashboard />} />
          <Route path="live" element={<AdminLive />} />
          <Route path="dispatch" element={<AdminDispatch />} />
          <Route path="disputes" element={<AdminDisputes />} />
          <Route path="riders" element={<AdminRiders />} />
          <Route path="onboarding" element={<AdminOnboarding />} />
          <Route path="kyc" element={<AdminKyc />} />
          <Route path="pricing" element={<AdminPricing />} />
          <Route path="requirements" element={<AdminRequirements />} />
          <Route path="clubs" element={<AdminClubs />} />
          <Route path="messaging" element={<AdminMessaging />} />
          <Route path="messages" element={<AdminMessageLog />} />
          <Route path="push" element={<AdminPushTest />} />
          <Route path="monitor" element={<AdminMonitor />} />
          <Route path="credits" element={<AdminCredits />} />
          <Route path="cities" element={<AdminCities />} />
          <Route path="merchant-sms" element={<AdminMerchantSms />} />
          <Route path="audit" element={<AdminAudit />} />
        </Route>
        <Route path="/rider/application" element={<RequireRole role="rider"><MyApplication /></RequireRole>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
